import { Account, AccountDescription, AuthState, GameID, gamesAPI, Player, PlayerID } from "@kosmod/sesame";
import { ReadOnlyRef, Ref, useEffect, useMemo, useRef, useState } from "@lukekaalim/act";
import { RoomSessionClient } from "../backend";
import { useAsync, useMappedClientData, useRefresh } from "@kosmod/kit";
import { HttpApi } from "@kosmod/pancake";

export type AudioSetup = {
  context: AudioContext,
  dest: MediaStreamAudioDestinationNode,
}

export type ConciergeState = {
  auth: AuthState,

  account: AccountDescription,
  self: Player,
  roomClient: RoomSessionClient,
  players: Player[],

  audio: {
    ready: boolean,

    localVoiceStream: Ref<null | MediaStream>,
    localVoiceNode: Ref<null | MediaStreamAudioSourceNode>,
    localVoiceVolume: Ref<number>,

    /**
     * Call this on user input, when you
     * are about to setup something that will use
     * an audio context destination
     */
    getOrCreateSetup(): AudioSetup,
  },
};


export const useConciergeState = (
  game: GameID,
  auth: AuthState,
  account: AccountDescription,
  roomClient: RoomSessionClient,
  self: Player,
  clients: { games: HttpApi.Client<typeof gamesAPI> }
): ConciergeState => {
  const [ready, setReady] = useState(false)

  const [refresh, refreshKey] = useRefresh()

  const localVoiceStream = useRef<null | MediaStream>(null);
  const localVoiceNode = useRef<null | MediaStreamAudioSourceNode>(null);
  const localVoiceVolume = useRef(0);

  const audioSetupRef = useRef<null | AudioSetup>(null);

  const element = useRef(() => document.createElement('audio')).get();

  const playerIds = useMappedClientData(roomClient.subjects.players, set => [...set], []);

  const { result: allPlayers } = useAsync(async () => {
    const { players } = await clients.games.players.GET({ game }, null)
    return players;
  }, [refreshKey])

  const mappedPlayers = useMemo(() => {
    if (!allPlayers)
      return [];
    return playerIds.map(id => allPlayers.find(p => p.id === id))
  }, [allPlayers, playerIds]);

  useEffect(() => {
    if (mappedPlayers.some(p => !p)) {
      refresh();
    }
  }, [playerIds])

  return {
    auth,
    account,
    self,
    roomClient,
    players: mappedPlayers.filter(x => !!x),
    
    audio: {
      ready,

      localVoiceStream,
      localVoiceNode,
      localVoiceVolume,

      getOrCreateSetup() {
        if (audioSetupRef.get())
          return audioSetupRef.get()!;

        const context = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive', });
        const dest = context.createMediaStreamDestination();

        element.srcObject = dest.stream;
        element.play();

        const setup = {
          context,
          dest
        }
        audioSetupRef.set(setup);
        setReady(true);
        return setup;
      },
    },
  }
};
